import { IconDocument, IconLanguage, IconPerson, IconStudySetting } from "@/assets/icons";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { Switch } from "@/components/elements/Switch";
import { SettingsCard, SettingsSection } from "@/components/commons/settings";
import { useStudySettings } from "@/infrastructure/adapters/user-preference/user-preference.hook";
import { UpdateStudySettingsPayload } from "@/infrastructure/adapters/user-preference/user-preference.types";
import { useLanguage } from "@/providers/LanguageProvider";
import { LANGUAGE_NAMES, SupportedLanguage, SUPPORTED_LANGUAGES } from "@/constants/languages";
import { TOption } from "@/types/common";
import { Button } from "@/components/elements/Button";
import { Link, Stack, Typography, Box, styled } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import LogoutIcon from "@mui/icons-material/Logout";
import Cookies from "js-cookie";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { text, state, background } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { getLogoutUrl } from "@/utils/api";
import { toast } from "@/utils/toast";
import { DailyStudyHoursInput } from "@/features/dashboard/components/DailyStudyHoursInput";
import { calculateDailyHoursFromWeeklyMinutes, calculateWeeklyMinutesFromDailyHours, isValidDailyHours, parseDailyHoursInput } from "@/features/dashboard/utils/studyTime";
import { DEFAULT_DAILY_STUDY_HOURS } from "@/features/dashboard/constants/studyTime";

const APP_VERSION = process.env.REACT_APP_VERSION || "1.0.0";

type TSettingsPageProps = {};

export const SettingsPage: FC<TSettingsPageProps> = () => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const { settings, updateSettings, isLoading } = useStudySettings();
  const [localSettings, setLocalSettings] = useState<UpdateStudySettingsPayload>({});
  const [dailyHoursInput, setDailyHoursInput] = useState<string>("");
  const [dailyHoursTouched, setDailyHoursTouched] = useState(false);

  // Initialize local settings
  useEffect(() => {
    if (settings) {
      setLocalSettings({
        isFuriganaEnabled: settings.isFuriganaEnabled,
        isTranslationEnabled: settings.isTranslationEnabled,
        isPushNotificationEnabled: settings.isPushNotificationEnabled,
        isRecommendationEnabled: settings.isRecommendationEnabled,
      });
      if (settings.weeklyStudyTimeTarget != null) {
        const dailyHrs = calculateDailyHoursFromWeeklyMinutes(settings.weeklyStudyTimeTarget);
        setDailyHoursInput(parseFloat(dailyHrs.toFixed(2)).toString());
      } else {
        setDailyHoursInput("");
      }
      setDailyHoursTouched(false);
    }
  }, [settings]);

  // Same validation shape as StudySettingsContent (onboarding): show inline error
  // only after the input has been touched and is non-empty.
  const dailyHoursErrorMessage = useMemo(() => {
    if (!dailyHoursTouched || dailyHoursInput === "") return null;
    const num = parseDailyHoursInput(dailyHoursInput);
    if (!isValidDailyHours(num)) return t("studySettings.studyTime.invalid_daily_target_error");
    return null;
  }, [dailyHoursInput, dailyHoursTouched, t]);

  // Autosave on blur. Empty → fall back to DEFAULT_DAILY_STUDY_HOURS (2h) and persist.
  // Invalid → skip API (inline error already visible).
  const handleDailyHoursBlur = async () => {
    setDailyHoursTouched(true);
    let num = parseDailyHoursInput(dailyHoursInput);
    if (dailyHoursInput === "") {
      num = DEFAULT_DAILY_STUDY_HOURS;
      setDailyHoursInput(String(num));
    }

    if (!isValidDailyHours(num)) return;
    const nextWeeklyMinutes = calculateWeeklyMinutesFromDailyHours(num);
    if (nextWeeklyMinutes === settings?.weeklyStudyTimeTarget) return;

    const success = await updateSettings({ weeklyStudyTimeTarget: nextWeeklyMinutes });
    if (success) toast.success(t("common.saved"));
  };

  // Handle setting changes (Auto-save) — send only changed field to match PATCH API
  const handleSettingChange = async (key: keyof UpdateStudySettingsPayload, value: boolean) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    await updateSettings({ [key]: value });
  };

  const languageOptions: TOption[] = SUPPORTED_LANGUAGES.map((lang) => ({
    value: lang,
    label: LANGUAGE_NAMES[lang],
  }));

  const handleLanguageChange = (value: string) => {
    changeLanguage(value as SupportedLanguage);
  };

  const handleLogout = useCallback(() => {
    window.location.href = getLogoutUrl();
  }, []);

  const learnerAccountId = Cookies.get("learner-account-id") ?? "";

  if (isLoading) return <LoadingScreen />;

  const isShowPrivacyPolicy = true;

  return (
    <Stack gap={2} sx={{ flex: 1 }}>
      <Typography sx={{ ...typography.head20Bold, color: text.primary }}>{t("settings.pageTitle")}</Typography>

      {/* Account */}
      <SettingsCard icon={<IconPerson fontSize="24px" />} title={t("settings.account.title")}>
        <SettingsSection title={t("settings.account.learnerId")} hasDivider={false}>
          <Input nonControl readonly value={learnerAccountId} placeholder="" />
        </SettingsSection>
      </SettingsCard>

      {/* Study Settings */}
      <SettingsCard icon={<IconStudySetting fontSize="24px" />} title={t("studySettings.title")}>
        <Stack gap={2}>
          <Stack gap={2.5}>
            <Switch
              nonControl
              label={t("studySettings.wordStudy.furiganaLabel")}
              labelVariant="body14Bold"
              checked={localSettings.isFuriganaEnabled ?? false}
              onChange={(_, checked) => handleSettingChange("isFuriganaEnabled", checked)}
            />
            <Switch
              nonControl
              label={t("studySettings.practice.translationLabel")}
              labelVariant="body14Bold"
              checked={localSettings.isTranslationEnabled ?? false}
              onChange={(_, checked) => handleSettingChange("isTranslationEnabled", checked)}
            />
          </Stack>
          <Stack gap={1}>
            <Typography sx={{ ...typography.body14Bold, color: text.primary }}>{t("studySettings.studyTime.settingsLabel")}</Typography>
            <DailyStudyHoursInput
              value={dailyHoursInput}
              onChange={(val) => {
                setDailyHoursInput(val);
                setDailyHoursTouched((touched) => !!val || touched);
              }}
              onBlur={handleDailyHoursBlur}
              errorMessage={dailyHoursErrorMessage}
            />
          </Stack>
        </Stack>
      </SettingsCard>

      {/* General Settings */}
      <SettingsCard icon={<IconLanguage fontSize="24px" />} title={t("settings.general.title")}>
        <SettingsSection title={t("settings.general.language")} hasDivider={false}>
          <Select size="lg" options={languageOptions} value={currentLanguage} onChange={handleLanguageChange} clearable={false} nonControl />
        </SettingsSection>

        <Stack gap={2}>
          <Switch
            nonControl
            label={t("settings.general.pushNotification")}
            subLabel={t("settings.general.pushNotificationDesc")}
            labelVariant="body14Bold"
            subLabelVariant="body14"
            checked={localSettings.isPushNotificationEnabled ?? false}
            onChange={(_, checked) => handleSettingChange("isPushNotificationEnabled", checked)}
          />
          <Switch
            nonControl
            label={t("settings.general.showRecommendation")}
            subLabel={t("settings.general.showRecommendationDesc")}
            labelVariant="body14Bold"
            subLabelVariant="body14"
            checked={localSettings.isRecommendationEnabled ?? true}
            onChange={(_, checked) => handleSettingChange("isRecommendationEnabled", checked)}
          />
        </Stack>
      </SettingsCard>

      {/* Legal */}
      <SettingsCard icon={<IconDocument fontSize="24px" />} title={t("settings.legal.title")} sx={{ gap: 1.5 }}>
        <StyledLink href="https://iro-hana.com/privacy/" target="_blank" underline="always">
          プライバシーポリシー（日本語のみ）
        </StyledLink>

        {!isShowPrivacyPolicy && (
          <StyledLink href="#" underline="always">
            プライバシーポリシー（翻訳文）
          </StyledLink>
        )}

        {/* <StyledLink
					component={RouterLink}
					to={AppRoutes.contentProvider}
					underline="always"
				>
					{t("settings.legal.contentProviderInfo")}
				</StyledLink> */}

        <StyledLink component={RouterLink} to={AppRoutes.license} underline="always">
          {t("settings.legal.licenseInfo")}
        </StyledLink>
      </SettingsCard>

      {/* Logout Button */}
      <Box display="flex" justifyContent="center" mt={1}>
        <Button variant="primary" size="md" startIcon={<LogoutIcon />} onClick={handleLogout}>
          {t("settings.logout")}
        </Button>
      </Box>

      {/* Footer - break out of parent padding to be full-width and flush with bottom nav */}
      <Box textAlign="center" mt="auto" mx={-2} mb={-2} bgcolor={background.white} py={2}>
        <Typography sx={{ ...typography.cap12, color: text.secondary }}>irohana study v{APP_VERSION}</Typography>
        <Typography sx={{ ...typography.cap12, color: text.secondary }}>© 2026 irohana Inc.</Typography>
      </Box>
    </Stack>
  );
};

export default SettingsPage;

const StyledLink = styled(Link)({
  ...typography.body14Semi,
  color: state.link_1,
  textDecorationColor: state.link_1,
}) as typeof Link;
